/**
 * TypeScript wrappers for WASM sculpt operations.
 *
 * - `loopToVertexNormals` — seeds per-vertex normals from per-corner loop
 *   normals by arithmetic mean.
 *
 * - `applyDab` — unified brush dispatch + auto-smooth for per-dab computation.
 *
 * - `initGrab` / `applyGrab` — grab brush (locked weights at stroke start).
 */

import type { SculptBrushType, SculptFalloffCurve } from "../app/storeTypes.ts";
import type { AdjacencyCache } from "../modeling/topology.ts";
import type {
  LoopNormals,
  PolygonTopology,
  VertexNormals,
} from "../modeling/types.ts";
import { asVertexNormals } from "../modeling/types.ts";
import {
  sculpt_apply_dab as wasmApplyDab,
  sculpt_apply_grab as wasmApplyGrab,
  sculpt_init_grab as wasmInitGrab,
  sculpt_loop_to_vertex_normals as wasmLoopToVertexNormals,
} from "./pkg/poseref_wasm.js";

// ---------------------------------------------------------------------------
// Brush / falloff enum mappings — must match sculpt_brushes.rs constants
// ---------------------------------------------------------------------------

const BRUSH_TYPE_MAP: Record<Exclude<SculptBrushType, "grab">, number> = {
  draw: 0,
  smooth: 1,
  flatten: 3,
};

const FALLOFF_CURVE_MAP: Record<SculptFalloffCurve, number> = {
  smooth: 0,
  sharp: 1,
  constant: 2,
  linear: 3,
};

// ---------------------------------------------------------------------------
// loopToVertexNormals
// ---------------------------------------------------------------------------

/**
 * Convert per-corner (loop) normals to per-vertex normals by arithmetic mean.
 *
 * Matches the TypeScript contract exactly — accumulate then divide by count,
 * not normalise — preserving the established sculpt-pipeline expectation.
 *
 * @param loopNormals  Per-corner normals (corner_count × 3).
 * @param topology     The mesh topology (only `corners` is accessed).
 * @param vertexCount  Number of unique vertices.
 */
export function loopToVertexNormals(
  loopNormals: LoopNormals,
  topology: PolygonTopology,
  vertexCount: number,
): VertexNormals {
  if (import.meta.env.DEV) {
    const expected = topology.corners.length * 3;
    if (loopNormals.length !== expected) {
      throw new Error(
        `loopToVertexNormals: loopNormals.length ${loopNormals.length} !== cornerCount*3 (${expected}). ` +
          `Per-vertex buffer passed instead of per-loop?`,
      );
    }
  }
  const raw = wasmLoopToVertexNormals(
    loopNormals as Float32Array,
    topology.corners,
    vertexCount,
  );
  return asVertexNormals(new Float32Array(raw));
}

// ---------------------------------------------------------------------------
// Brush params packing
// ---------------------------------------------------------------------------

/** Reusable 12-element buffer to avoid per-dab allocation. */
const _packedParams = new Float32Array(12);

/**
 * Pack brush parameters into the flat f32 layout expected by the Rust side:
 * `[cx,cy,cz, nx,ny,nz, radius, strength, pressure, invert, falloffCurve, autoSmooth]`
 */
function packParams(
  center: [number, number, number],
  normal: [number, number, number],
  radius: number,
  strength: number,
  pressure: number,
  invert: boolean,
  falloffCurve: SculptFalloffCurve,
  autoSmooth: number,
): Float32Array {
  _packedParams[0] = center[0];
  _packedParams[1] = center[1];
  _packedParams[2] = center[2];
  _packedParams[3] = normal[0];
  _packedParams[4] = normal[1];
  _packedParams[5] = normal[2];
  _packedParams[6] = radius;
  _packedParams[7] = strength;
  _packedParams[8] = pressure;
  _packedParams[9] = invert ? 1.0 : 0.0;
  _packedParams[10] = FALLOFF_CURVE_MAP[falloffCurve];
  _packedParams[11] = autoSmooth;
  return _packedParams;
}

// ---------------------------------------------------------------------------
// applyDab
// ---------------------------------------------------------------------------

/**
 * Apply a sculpt brush dab with optional auto-smooth.
 *
 * Returns N×3 displacement **deltas** for the affected vertices. The caller
 * adds these to the live mesh positions.
 */
export function applyDab(
  brushType: SculptBrushType,
  vertices: Float32Array,
  vertexNormals: Float32Array,
  affected: Uint32Array,
  center: [number, number, number],
  normal: [number, number, number],
  radius: number,
  strength: number,
  pressure: number,
  invert: boolean,
  falloffCurve: SculptFalloffCurve,
  autoSmooth: number,
  adjCache: AdjacencyCache,
  edges: Uint32Array,
  vertexMask: Float32Array,
): Float32Array {
  if (brushType === "grab") {
    throw new Error("applyDab does not support the grab brush.");
  }
  const params = packParams(
    center,
    normal,
    radius,
    strength,
    pressure,
    invert,
    falloffCurve,
    autoSmooth,
  );
  const raw = wasmApplyDab(
    BRUSH_TYPE_MAP[brushType],
    vertices,
    vertexNormals,
    affected,
    params,
    adjCache.vertEdgeOffsets,
    adjCache.vertEdgeIndices,
    edges,
    vertexMask,
  );
  return new Float32Array(raw);
}

// ---------------------------------------------------------------------------
// initGrab / applyGrab
// ---------------------------------------------------------------------------

/**
 * Compute per-vertex grab weights at stroke start.  Returns N weights.
 */
export function initGrab(
  vertices: Float32Array,
  affected: Uint32Array,
  center: [number, number, number],
  radius: number,
  strength: number,
  falloffCurve: SculptFalloffCurve,
  vertexMask: Float32Array,
): Float32Array {
  const params = packParams(
    center,
    [0, 0, 0],
    radius,
    strength,
    1.0,
    false,
    falloffCurve,
    0,
  );
  const raw = wasmInitGrab(vertices, affected, params, vertexMask);
  return new Float32Array(raw);
}

/**
 * Apply grab displacement using locked weights.  Returns N×3 **absolute**
 * displacement values — the caller writes them directly to the live mesh.
 */
export function applyGrab(
  weights: Float32Array,
  initialDisp: Float32Array,
  deltaX: number,
  deltaY: number,
  deltaZ: number,
): Float32Array {
  const raw = wasmApplyGrab(weights, initialDisp, deltaX, deltaY, deltaZ);
  return new Float32Array(raw);
}
